import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './GuideDialog.css';

export type GuideSlide = { title: string; body: string; content: React.ReactNode; preview?: React.ReactNode };

type GuideEntry = { hidden: boolean; select: (selected: boolean) => void; close: (never: boolean) => void };
const guides = new Map<string, Map<symbol, GuideEntry>>();
const selectGuide = (entries: Map<symbol, GuideEntry>) => {
    const values = Array.from(entries.values());
    const owner = values.find((entry) => !entry.hidden) ?? values[0];
    values.forEach((entry) => entry.select(entry === owner));
};

// 서로 다른 안내가 겹치면 실제 쌓인 순서대로 맨 위 안내만 키보드를 받아요.
const topGuide = () => Array.from(document.querySelectorAll<HTMLElement>('[data-guide-dialog]'))
    .filter((panel) => panel.parentElement?.getAttribute('aria-hidden') !== 'true')
    .reduce<HTMLElement | null>((top, panel) => !top || Number(panel.parentElement?.style.zIndex) >= Number(top.parentElement?.style.zIndex) ? panel : top, null);

/** 포털은 부모 화면 밖에 붙으므로, 같은 안내의 표시 소유권도 화면 밖에서 관리한다. */
const useGuideOwner = (badge: string, hidden: boolean, onClose: (never: boolean) => void) => {
    const [selected, setSelected] = useState(false);
    const closeRef = useRef(onClose);
    useLayoutEffect(() => { closeRef.current = onClose; }, [onClose]);
    useLayoutEffect(() => {
        const id = Symbol(badge);
        const entries = guides.get(badge) ?? new Map<symbol, GuideEntry>();
        guides.set(badge, entries);
        entries.set(id, { hidden, select: setSelected, close: (never) => closeRef.current(never) });
        selectGuide(entries);
        return () => {
            entries.delete(id);
            if (!entries.size) guides.delete(badge);
            else selectGuide(entries);
        };
    }, [badge, hidden]);
    const close = useCallback((never: boolean) => {
        // 하나만 닫으면 대기 중이던 안내가 1장부터 다시 나타난다. 동일 안내를 함께 닫는다.
        Array.from(guides.get(badge)?.values() ?? []).forEach((entry) => entry.close(never));
    }, [badge]);
    return { selected, close };
};

/**
 * 여러 장을 넘겨 보는 화면 안내 팝업(세션 현황 안내·예외 수업료 안내가 같이 쓴다).
 * - 맨 위에 뜬다: Esc 는 이 안내만 닫고 뒤의 창(회원 상세·요율 창 등)으로 넘기지 않는다.
 * - hidden 이면 내리지 않고 숨기기만 해서 보던 장·「다시 보지 않기」 체크가 남는다(다른 팝업이 잠깐 덮을 때).
 */
const GuideDialog = ({
    badge,
    slides,
    onClose,
    hidden = false,
    minHeight = 420,
    zIndex = 1000,
    showNever = true,
    variant = 'default',
}: {
    badge: string;
    slides: GuideSlide[];
    onClose: (never: boolean) => void;
    hidden?: boolean;
    minHeight?: number;
    /** 다른 모달 위에 띄울 때(예: 요율 창 z 99998) 더 높게 준다. */
    zIndex?: number;
    /** 「다시 보지 않기」 체크. 한 번만 자동으로 뜨는 안내는 필요 없어 숨긴다. */
    showNever?: boolean;
    /** 세션 안내의 화면 미리보기·고정 높이. 예외 수업료 안내는 기존 모양을 유지한다. */
    variant?: 'default' | 'preview';
}) => {
    const [index, setIndex] = useState(0);
    const [never, setNever] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const slide = slides[Math.min(index, slides.length - 1)];
    const last = index >= slides.length - 1;
    const preview = variant === 'preview';
    const { selected, close } = useGuideOwner(badge, hidden, onClose);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [index]);

    useEffect(() => {
        if (hidden || !selected) return;
        const panel = panelRef.current;
        if (panel !== topGuide()) return;
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        panel?.querySelector<HTMLButtonElement>('button')?.focus();
        return () => {
            // 다른 팝업이 이미 포커스를 가져갔다면 그 팝업의 포커스를 유지해요.
            if (previousFocus?.isConnected && (panel?.contains(document.activeElement) || document.activeElement === document.body)) {
                previousFocus.focus();
            }
        };
    }, [hidden, selected]);

    useEffect(() => {
        if (hidden || !selected) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (panelRef.current !== topGuide()) return;
            if (event.key === 'Tab') {
                const targets = panelRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), [tabindex="0"]');
                if (!targets?.length) return;
                const first = targets[0];
                const last = targets[targets.length - 1];
                if (!panelRef.current?.contains(document.activeElement) || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
                    event.preventDefault();
                    (event.shiftKey ? last : first).focus();
                }
                return;
            }
            if (event.key !== 'Escape') return;
            event.stopImmediatePropagation();
            event.preventDefault();
            close(never);
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [hidden, selected, never, close]);

    if (!selected) return null;

    // body 로 띄운다 — 부모 모달의 스크롤·overflow 에 잘리지 않게.
    return createPortal(
        <div
            style={{ zIndex }}
            className={`fixed inset-0 items-center justify-center bg-[rgba(0,0,0,0.45)] px-[16px] ${hidden ? 'hidden' : 'flex'}`}
            aria-hidden={hidden}
            onClick={() => close(never)}
        >
            <div
                ref={panelRef}
                data-guide-dialog={badge}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className={`w-full rounded-[24px] shadow-[0_20px_60px_rgba(0,0,0,0.2)] overflow-hidden ${preview ? 'guide-dialog-preview' : 'max-w-[680px] bg-[#F9FAFB]'}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={preview ? 'guide-dialog-preview-header' : 'flex items-center justify-between px-[32px] pt-[26px]'}>
                    <span className={preview ? 'sr-only' : 'rounded-full bg-[#E8F0FF] px-[12px] py-[5px] text-[13px] font-semibold text-[#1B4FD8]'}>{badge}</span>
                    <div className="flex items-center gap-[14px]">
                        <span className={preview ? 'sr-only' : 'text-[13px] text-[#8B95A1]'}>{`${index + 1} / ${slides.length}`}</span>
                        <button type="button" aria-label="닫기" onClick={() => close(never)} className="w-[32px] h-[32px] rounded-full text-[20px] leading-[32px] text-[#6B7684] hover:bg-[#EEF0F3]">
                            ×
                        </button>
                    </div>
                </div>
                <div ref={bodyRef} tabIndex={preview ? 0 : undefined} className={preview ? 'guide-dialog-preview-body' : 'px-[32px] pt-[18px] pb-[24px] flex flex-col'} style={preview ? undefined : { minHeight }}>
                    {preview && slide.preview}
                    <h2 id={titleId} className="text-[24px] leading-[34px] font-semibold text-[#191F28]">
                        {slide.title}
                    </h2>
                    <p className={`mt-[8px] mb-[20px] leading-[24px] text-[#4E5968] break-keep ${preview ? 'text-[16px]' : 'text-[15px]'}`}>{slide.body}</p>
                    <div className="flex-1">{slide.content}</div>
                </div>
                <div className={`flex items-center justify-between gap-[12px] border-t border-solid border-[#EEF0F3] bg-white ${preview ? 'guide-dialog-preview-footer' : 'px-[32px] py-[18px]'}`}>
                    {showNever && (!preview || last) ? (
                        <label className="flex items-center gap-[8px] text-[14px] text-[#6B7684] cursor-pointer select-none">
                            <input type="checkbox" checked={never} onChange={(e) => setNever(e.target.checked)} className="w-[18px] h-[18px]" />
                            다시 보지 않기
                        </label>
                    ) : (
                        <span />
                    )}
                    <div className="flex items-center gap-[6px]" aria-hidden="true">
                        {slides.map((s, i) => (
                            <span key={s.title} className={`h-[8px] rounded-full transition-all ${i === index ? 'w-[22px] bg-[#3182F6]' : 'w-[8px] bg-[#D1D6DB]'}`} />
                        ))}
                    </div>
                    <div className="flex items-center gap-[8px]">
                        {index > 0 && (
                            <button type="button" onClick={() => setIndex((i) => i - 1)} className="min-h-[44px] rounded-[12px] px-[18px] text-[15px] text-[#4E5968] bg-[#F2F4F6] hover:bg-[#E5E8EB]">
                                이전
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => (last ? close(never) : setIndex((i) => i + 1))}
                            className={`min-h-[44px] rounded-[12px] px-[22px] text-[15px] font-semibold text-white bg-[#3182F6] hover:bg-[#1B64DA] ${preview ? 'guide-dialog-primary' : ''}`}
                        >
                            {last ? '시작하기' : '다음'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default GuideDialog;
